import React, { CSSProperties, useEffect, useRef, useState } from 'react';

import { useAppSelector } from 'hooks';

/**
 * Shows a toast when a seat offer expires OR the player is bumped to the
 * back of the queue. v2 gets explicit feedback that Unity conveys via the
 * ring snapping on BumpCount change — important here because v2's modal
 * just disappears, leaving the player wondering what happened.
 *
 * Trigger: for any of my waitlist entries, `bumpCount` strictly increases.
 * The engine bumps on timeout and on re-offer, so both cases are covered.
 * Mounted globally (in App.tsx) so it works regardless of which
 * table the player is currently viewing.
 *
 * Plain-div render (not MUI Snackbar) to avoid Portal/Transition pitfalls
 * in jsdom tests; the semantics that matter — role='alert', dismissible,
 * auto-hide — are inlined below.
 */
const AUTO_HIDE_MS = 6000;

const SeatOfferTimeoutSnackbar: React.FC = () => {
  const mySpotByGameId = useAppSelector((state) => state.waitlist.mySpotByGameId);
  const prevBumps = useRef<Record<number, number>>({});
  const [open, setOpen] = useState(false);
  const [message, setMessage] = useState('');

  useEffect(() => {
    for (const [gameIdStr, entry] of Object.entries(mySpotByGameId)) {
      const gameId = Number(gameIdStr);
      const prev = prevBumps.current[gameId];
      if (prev === undefined) {
        prevBumps.current[gameId] = entry.bumpCount;
        continue;
      }
      if (entry.bumpCount > prev) {
        const msg =
          entry.waitlistCount > 0
            ? `Seat offer timed out — you've been moved to position ${entry.position} of ${entry.waitlistCount}.`
            : `Seat offer timed out — you've been moved to the back of the waitlist.`;
        setMessage(msg);
        setOpen(true);
        prevBumps.current[gameId] = entry.bumpCount;
      }
    }
    for (const gameIdStr of Object.keys(prevBumps.current)) {
      const gameId = Number(gameIdStr);
      if (!mySpotByGameId[gameId]) {
        delete prevBumps.current[gameId];
      }
    }
  }, [mySpotByGameId]);

  useEffect(() => {
    if (!open) return;
    const t = window.setTimeout(() => setOpen(false), AUTO_HIDE_MS);
    return () => window.clearTimeout(t);
  }, [open]);

  if (!open) return null;

  return (
    <div
      role='alert'
      aria-label='seat-offer-timeout-alert'
      data-testid='seat-offer-timeout-snackbar'
      style={snackbarStyle}>
      <span style={iconStyle} aria-hidden='true'>
        ⏳
      </span>
      <span style={{ flex: 1 }}>{message}</span>
      <button
        type='button'
        aria-label='dismiss-seat-offer-timeout'
        style={dismissStyle}
        onClick={() => setOpen(false)}>
        ✕
      </button>
    </div>
  );
};

export default SeatOfferTimeoutSnackbar;

// Matches the shared branded GameModal (dark navy card, gold accent, white
// text, rounded + blurred) so this toast reads as part of the same system as
// the other waitlist modals — just non-blocking and bottom-anchored.
const snackbarStyle: CSSProperties = {
  position: 'fixed',
  bottom: '24px',
  left: '50%',
  transform: 'translateX(-50%)',
  display: 'inline-flex',
  alignItems: 'center',
  gap: '0.75rem',
  padding: '0.85rem 1rem 0.85rem 1.1rem',
  backgroundColor: 'rgba(20, 30, 42, 0.98)',
  backdropFilter: 'blur(16px)',
  color: 'rgba(255, 255, 255, 0.9)',
  border: '1px solid rgba(233, 140, 38, 0.35)',
  borderRadius: '16px',
  boxShadow: '0 16px 48px rgba(0, 0, 0, 0.6)',
  fontFamily: "'Outfit', sans-serif",
  fontSize: '0.9rem',
  fontWeight: 500,
  lineHeight: 1.45,
  zIndex: 1400,
  maxWidth: 'min(90vw, 360px)'
};

const iconStyle: CSSProperties = {
  fontSize: '1.15rem',
  color: '#E98C26',
  flexShrink: 0
};

// Circular subtle close, mirroring GameModal's floating close button.
const dismissStyle: CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: '1.6rem',
  height: '1.6rem',
  flexShrink: 0,
  background: 'rgba(255, 255, 255, 0.06)',
  border: '1px solid rgba(255, 255, 255, 0.1)',
  borderRadius: '50%',
  color: 'rgba(255, 255, 255, 0.6)',
  cursor: 'pointer',
  fontSize: '0.9rem',
  fontWeight: 700,
  fontFamily: 'inherit'
};
